import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CreditCard, Wallet, Phone } from 'lucide-react';
import { sendTicketEmail } from '../utils/emailService';
import { deviceCategories } from '../utils/constants';
import { useTicketStore } from '../stores/ticketStore';
import { supabase } from '../lib/supabase';

interface FormData {
  name: string;
  email: string;
  phone: string;
  category: string;
  make: string;
  model: string;
  serialNumber: string;
  date: string;
  time: string;
  description: string;
  price?: number;
}

const paymentMethods = [
  { name: 'Cash', icon: Wallet },
  { name: 'Credit/Debit Cards', icon: CreditCard },
  { name: 'Apple Pay', icon: Phone },
  { name: 'PayPal', icon: Wallet },
  { name: 'Cash App', icon: Wallet },
  { name: 'Zelle', icon: Phone }
];

export default function SubmitTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const addTicket = useTicketStore(state => state.addTicket);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    category: '',
    make: '',
    model: '',
    serialNumber: '',
    date: '',
    time: '',
    description: ''
  });

  useEffect(() => {
    const state = location.state as { selectedCategory?: string };
    if (state?.selectedCategory) {
      setSelectedCategory(state.selectedCategory);
      setFormData(prev => ({
        ...prev,
        category: state.selectedCategory || ''
      }));
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Create ticket in Supabase
      const { data: ticket, error } = await supabase
        .from('tickets')
        .insert({
          customer: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: formData.category,
          description: formData.description,
          make: formData.make,
          model: formData.model,
          serial_number: formData.serialNumber,
          preferred_date: formData.date,
          preferred_time: formData.time,
          status: 'open',
          priority: 'medium'
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local store
      await addTicket({
        customer: formData.name,
        email: formData.email,
        phone: formData.phone,
        category: formData.category,
        description: formData.description,
        make: formData.make,
        model: formData.model,
        serialNumber: formData.serialNumber,
        preferredDate: formData.date,
        preferredTime: formData.time,
        priority: 'medium'
      });

      // Send email notification
      await sendTicketEmail({
        customerName: formData.name,
        customerEmail: formData.email,
        ticketId: ticket.id,
        category: formData.category,
        description: formData.description,
        date: new Date().toISOString(),
        phone: formData.phone,
        preferredDate: formData.date,
        preferredTime: formData.time,
        make: formData.make,
        model: formData.model,
        serialNumber: formData.serialNumber
      });

      toast.success('Ticket submitted successfully! We will contact you shortly.');
      navigate('/ticket-submitted');
    } catch (error) {
      console.error('Failed to submit ticket:', error);
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setSelectedMake('');
    setFormData(prev => ({
      ...prev,
      category,
      make: '',
      model: ''
    }));
  };

  const handleMakeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const make = e.target.value;
    setSelectedMake(make);
    setFormData(prev => ({
      ...prev,
      make,
      model: ''
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Submit a Support Ticket</h1>
          <p className="text-xl text-gray-600">
            Tell us about your technical issue and we'll help you resolve it
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Category *
              </label>
              <select
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              >
                <option value="">Select a category</option>
                {deviceCategories.map(cat => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category}
                  </option>
                ))}
              </select>
            </div>

            {selectedCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Make/Brand
                  </label>
                  <select
                    value={selectedMake}
                    onChange={handleMakeChange}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  >
                    <option value="">Select make/brand</option>
                    {deviceCategories
                      .find(cat => cat.category === selectedCategory)
                      ?.makes.map(make => (
                        <option key={make.name} value={make.name}>
                          {make.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <select
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    disabled={!selectedMake}
                  >
                    <option value="">Select model</option>
                    {deviceCategories
                      .find(cat => cat.category === selectedCategory)
                      ?.makes.find(make => make.name === selectedMake)
                      ?.models.map(model => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serial Number (if applicable)
              </label>
              <input
                type="text"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Time
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Please describe your issue in detail..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accepted Payment Methods
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {paymentMethods.map((method) => (
                  <div
                    key={method.name}
                    className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg"
                  >
                    <method.icon className="w-5 h-5 text-primary" />
                    <span className="text-sm">{method.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>We'll review your ticket and contact you shortly.</p>
          <p>For immediate assistance, please call us directly.</p>
        </div>
      </div>
    </div>
  );
}